import { Link, useParams } from "react-router";
import { ArrowLeft, Filter } from "lucide-react";
import {
  SEGMENT_ENTITY_LABEL,
  type FilterTree,
  type SegmentEntityType,
  countConditions,
  defaultFilter,
  isFilterGroup,
} from "@colab/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import { useSegment } from "@/features/settings/use-segment";
import { EditSegmentForm } from "./_components/edit-segment-form";
import { DeleteSegmentButton } from "./_components/delete-segment-button";

// Phase 7 sub-bucket 18 — SPA port of /app/admin/settings/segments/[id]/page.tsx.
// FilterTreeBuilder editor inside <EditSegmentForm>. entity_type is locked
// after creation — flipping it would invalidate stored fields.

export function SegmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const session = useAuth().session;
  const canManage = !!session?.permissions.includes("settings.write");

  const query = useSegment(id);
  const segment = query.data;

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!segment) {
    return (
      <EmptyState
        icon={Filter}
        title="Segment not found"
        description="This segment may have been deleted. Return to the list to see what's available."
      />
    );
  }

  const entityType = segment.entity_type as SegmentEntityType;
  const initialFilter: FilterTree = isFilterGroup(segment.filter)
    ? (segment.filter as FilterTree)
    : defaultFilter(entityType);
  const conditionCount = countConditions(initialFilter);

  return (
    <div>
      <Link
        to="/admin/settings/segments"
        className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to segments
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight">
            {segment.name}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              {SEGMENT_ENTITY_LABEL[entityType]}
            </span>
            <span>
              {conditionCount}{" "}
              {conditionCount === 1 ? "condition" : "conditions"}
            </span>
            <span>Updated {formatDate(segment.updated_at)}</span>
          </div>
        </div>
        {canManage && <DeleteSegmentButton id={segment.id} />}
      </div>

      <EditSegmentForm
        segmentId={segment.id}
        entityType={entityType}
        name={segment.name}
        description={segment.description}
        initialFilter={initialFilter}
        canManage={canManage}
      />
    </div>
  );
}
